import React from 'react';
import { Briefcase, Globe2, Radio, GraduationCap } from 'lucide-react';
import { Section, Eyebrow, H2, Reveal, FONT, MONO } from './ui';

const AREA = {
  corporate: { Icon: Briefcase, tone: 'from-blue-600 to-blue-800' },
  international: { Icon: Globe2, tone: 'from-cyan-500 to-blue-700' },
  media: { Icon: Radio, tone: 'from-amber-400 to-orange-600' },
  academia: { Icon: GraduationCap, tone: 'from-indigo-500 to-slate-800' },
};

const Photos = ({ photos, L }) => (
  <div className="relative h-[420px] sm:h-[520px]" data-testid="whois-photos">
    {photos.slice(0, 2).map((p, i) => (
      <figure key={p.src} className={`absolute overflow-hidden rounded-[2rem] shadow-2xl ring-1 ring-black/5 transition-transform duration-500 hover:rotate-0 hover:scale-[1.02] ${i === 0 ? 'left-0 top-0 w-[72%] h-[82%] -rotate-2 z-10' : 'right-0 bottom-0 w-[52%] h-[56%] rotate-3 z-20 border-[6px] border-[#f7f3ec]'}`} data-testid={`whois-photo-${i}`}>
        <img src={p.src} alt={L(p.caption)} className="w-full h-full object-cover" />
        <figcaption className="absolute left-3 bottom-3 px-3 py-1 rounded-full bg-black/55 backdrop-blur text-[11px] font-semibold text-white" style={MONO}>{L(p.caption)}</figcaption>
      </figure>
    ))}
  </div>
);

const AreaCard = ({ a, i, L }) => {
  const { Icon, tone } = AREA[a.key] || AREA.corporate;
  return (
    <Reveal delay={120 * i}>
      <article className="group h-full rounded-3xl bg-white/80 border border-slate-200/80 p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`whois-area-${a.key}`}>
        <div className="flex items-center gap-3">
          <span className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${tone} flex items-center justify-center shadow-lg transition-transform group-hover:scale-110`}><Icon className="w-5 h-5 text-white" /></span>
          <h3 className="text-lg font-extrabold text-slate-900" style={FONT}>{L(a.title)}</h3>
          <span className="ml-auto text-xs text-slate-400" style={MONO}>0{i + 1}</span>
        </div>
        <ul className="mt-4 space-y-2.5">
          {L(a.items).map((it) => <li key={it} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />{it}</li>)}
        </ul>
      </article>
    </Reveal>
  );
};

export const WhoIsSpeaking = ({ d, L }) => {
  const w = d.whois;
  if (!w) return null;
  return (
    <Section id="whois" className="!bg-[#f7f3ec] overflow-hidden">
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
        <Reveal><Photos photos={w.photos || []} L={L} /></Reveal>
        <Reveal delay={120}>
          <Eyebrow>{L(w.eyebrow)}</Eyebrow>
          <H2 className="mt-4" data-testid="whois-title">{L(w.title)}</H2>
          <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
            {(w.stats || []).map((s) => (
              <div key={s.value} className="border-l-2 border-amber-500 pl-4" data-testid="whois-stat">
                <p className="text-4xl font-extrabold text-blue-700" style={FONT}>{s.value}</p>
                <p className="mt-1 text-xs text-slate-600 leading-snug">{L(s.label)}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
      <div className="mt-14 grid md:grid-cols-2 gap-5">
        {w.areas.map((a, i) => <AreaCard key={a.key} a={a} i={i} L={L} />)}
      </div>
    </Section>
  );
};
